import { useEffect, useMemo, useState } from "react";
import type { KonsolidiertePosition } from "@bidi-handel/core/schemas";
import { Icon } from "../../shell/Icon";
import {
  buildOzGroups,
  countItems,
  type OzGroupNode,
} from "./oz-tree";

/**
 * LV-Baumstruktur-Modal — öffnet sich vom Klick auf die POSITIONEN-Card im
 * Detail-Hero. Lädt phase/3 (anfrage-konsolidiert.json) und zeigt eine
 * navigierbare Hierarchie:
 *   Gewerk · Hauptgruppe → Unter-Hauptgruppe → Position-Details (OZ, Originaltext,
 *   Langtext, Menge/Einheit).
 *
 * Hauptgruppen-Namen kommen aus dem GAEB-LblTx-Feld bzw. der Vision-Erkennung
 * im PDF-LV-Extraktor (Sprint 4b). Bei Pipeline-Outputs ohne `hauptgruppe`
 * fallen wir zurück auf "Gruppe <oz-präfix>".
 */

export function LvTreeModal({
  tenantId,
  anfrageId,
  onClose,
}: {
  tenantId: string;
  anfrageId: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<{
    positionen: KonsolidiertePosition[];
    duplikateEntfernt?: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => new Set());
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(
        anfrageId
      )}/phase/3`
    )
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as {
          positionen: KonsolidiertePosition[];
          duplikateEntfernt?: number;
        };
      })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, anfrageId]);

  const tree = useMemo(
    () =>
      data
        ? buildOzGroups(
            data.positionen,
            (p) => p.oz,
            (p) => p.hauptgruppe ?? undefined
          )
        : [],
    [data]
  );

  function toggleGroup(path: string) {
    setClosedGroups((cur) => {
      const next = new Set(cur);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }
  function toggleItem(positionId: string) {
    setExpandedItems((cur) => {
      const next = new Set(cur);
      if (next.has(positionId)) next.delete(positionId);
      else next.add(positionId);
      return next;
    });
  }

  const total = data?.positionen.length ?? 0;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 980 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="uppercase-mini">/ ANFRAGE / LV-STRUKTUR</div>
            <div className="modal-title">Leistungsverzeichnis</div>
            <div className="dim mono" style={{ fontSize: 11, marginTop: 6 }}>
              {total} Positionen konsolidiert · Hauptgruppen aus GAEB-LblTx / PDF-Vision
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="modal-body" style={{ maxHeight: "70vh" }}>
          {loading && (
            <div className="dim" style={{ padding: 24 }}>
              <span className="spinner" style={{ borderColor: "var(--acc)", borderRightColor: "transparent" }} />{" "}
              LV wird geladen …
            </div>
          )}
          {error && (
            <div
              className="warn-line"
              style={{
                borderLeftColor: "var(--bad)",
                background: "oklch(0.72 0.18 25 / 0.08)",
              }}
            >
              <strong>LV nicht ladbar:</strong>{" "}
              <span className="mono">{error}</span>{" "}
              {error.includes("404") && (
                <span>— Phase 3 (Konsolidierung) ist noch nicht gelaufen.</span>
              )}
            </div>
          )}
          {!loading && !error && tree.length === 0 && (
            <div className="dim">Keine Positionen in der Konsolidierung.</div>
          )}
          {!loading && !error && tree.length > 0 && (
            <div className="lv-tree" style={{ display: "grid", gap: 4 }}>
              {tree.map((group) => (
                <LvGroup
                  key={group.path || "_root"}
                  group={group}
                  closedGroups={closedGroups}
                  expandedItems={expandedItems}
                  onToggleGroup={toggleGroup}
                  onToggleItem={toggleItem}
                />
              ))}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <span className="dim mono" style={{ fontSize: 11 }}>
            Klick auf Gruppe = ein-/ausklappen · Klick auf Position = Langtext zeigen
          </span>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn" onClick={onClose}>
            Schliessen
          </button>
        </div>
      </div>
    </div>
  );
}

function LvGroup({
  group,
  closedGroups,
  expandedItems,
  onToggleGroup,
  onToggleItem,
}: {
  group: OzGroupNode<KonsolidiertePosition>;
  closedGroups: Set<string>;
  expandedItems: Set<string>;
  onToggleGroup: (path: string) => void;
  onToggleItem: (positionId: string) => void;
}) {
  const hasHeader = group.path !== "";
  const isOpen = !hasHeader || !closedGroups.has(group.path);
  const total = countItems(group);
  const title = group.hauptgruppe ?? `Gruppe ${group.path}`;

  if (!hasHeader) {
    return (
      <>
        {group.items.map((p) => (
          <LvItem
            key={p.positionId}
            item={p}
            expanded={expandedItems.has(p.positionId)}
            onToggle={() => onToggleItem(p.positionId)}
          />
        ))}
        {group.children.map((child) => (
          <LvGroup
            key={child.path}
            group={child}
            closedGroups={closedGroups}
            expandedItems={expandedItems}
            onToggleGroup={onToggleGroup}
            onToggleItem={onToggleItem}
          />
        ))}
      </>
    );
  }

  const indent = group.depth * 16;
  return (
    <>
      <button
        type="button"
        onClick={() => onToggleGroup(group.path)}
        style={{
          display: "grid",
          gridTemplateColumns: `${indent}px 18px 70px 1fr auto`,
          gap: 10,
          alignItems: "center",
          padding: "10px 12px",
          background: group.depth === 0 ? "var(--bg-elevated)" : "var(--bg-raised)",
          border: "1px solid var(--line-soft)",
          borderRadius: "var(--r)",
          cursor: "pointer",
          font: "inherit",
          color: "inherit",
          textAlign: "left",
          marginTop: group.depth === 0 ? 6 : 0,
        }}
      >
        <span />
        <span className="mono" style={{ color: "var(--fg-dim)", fontSize: 12 }}>
          {isOpen ? "▾" : "▸"}
        </span>
        <span
          className="mono"
          style={{ color: "var(--fg-mute)", fontSize: 12 }}
        >
          {group.path}
        </span>
        <span
          style={{
            fontWeight: group.depth === 0 ? 600 : 500,
            color: "var(--fg)",
          }}
        >
          {title}
        </span>
        <span
          className="mono"
          style={{ color: "var(--fg-faint)", fontSize: 11 }}
        >
          {total} Pos
        </span>
      </button>
      {isOpen && (
        <>
          {group.items.map((p) => (
            <LvItem
              key={p.positionId}
              item={p}
              expanded={expandedItems.has(p.positionId)}
              onToggle={() => onToggleItem(p.positionId)}
              indent={indent + 24}
            />
          ))}
          {group.children.map((child) => (
            <LvGroup
              key={child.path}
              group={child}
              closedGroups={closedGroups}
              expandedItems={expandedItems}
              onToggleGroup={onToggleGroup}
              onToggleItem={onToggleItem}
            />
          ))}
        </>
      )}
    </>
  );
}

function LvItem({
  item,
  expanded,
  onToggle,
  indent = 12,
}: {
  item: KonsolidiertePosition;
  expanded: boolean;
  onToggle: () => void;
  indent?: number;
}) {
  const hasLangtext = !!item.kurzText && item.kurzText.trim().length > 0;

  return (
    <div style={{ paddingLeft: indent }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: "grid",
          gridTemplateColumns: "100px 1fr 90px",
          gap: 12,
          alignItems: "start",
          padding: "10px 12px",
          background: expanded ? "var(--bg-raised)" : "transparent",
          border: "1px solid transparent",
          borderRadius: "var(--r)",
          cursor: hasLangtext ? "pointer" : "default",
          font: "inherit",
          color: "inherit",
          textAlign: "left",
          width: "100%",
        }}
        disabled={!hasLangtext}
      >
        <span className="mono" style={{ color: "var(--fg-mute)", fontSize: 12 }}>
          {item.oz}
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ color: "var(--fg)", fontSize: 13 }}>
            {item.originaltext}
          </span>
          {hasLangtext && expanded && (
            <span
              style={{
                display: "block",
                marginTop: 6,
                padding: "8px 12px",
                background: "var(--bg-base)",
                border: "1px solid var(--line-soft)",
                borderLeft: "3px solid var(--acc)",
                borderRadius: "var(--r-sm)",
                color: "var(--fg-mute)",
                fontSize: 12.5,
                whiteSpace: "pre-line",
                lineHeight: 1.55,
              }}
            >
              {item.kurzText}
            </span>
          )}
          {hasLangtext && !expanded && (
            <span
              className="mono"
              style={{
                display: "block",
                marginTop: 2,
                color: "var(--fg-faint)",
                fontSize: 11,
              }}
            >
              ▸ Langtext ({item.kurzText!.length} Z.)
            </span>
          )}
        </span>
        <span
          className="mono"
          style={{
            color: "var(--fg)",
            fontSize: 12,
            textAlign: "right",
          }}
        >
          {item.menge} {item.einheit}
        </span>
      </button>
    </div>
  );
}

// Reserved for future use — wir lassen Icon importiert für mögliche Folge-Buttons.
void Icon;
